import React from 'react';

/**
 * React.lazy that survives a deploy.
 *
 * Every route is code-split, so each page is a chunk whose filename carries a content
 * hash, and a deploy replaces all of them. A tab opened beforehand still holds the old
 * index.html and asks for chunks that are no longer on the server — the import rejects,
 * and without this the whole tree unmounts and the user is left looking at nothing.
 *
 * Three steps, in order of how cheap they are, so the expensive one is genuinely a last
 * resort rather than the first answer:
 *
 *   retry     a dropped request, a flaky connection, a proxy hiccup. Two attempts with a
 *             short pause fix these and the user sees nothing at all.
 *   reload    the chunk is really gone, which means the tab is stale. Reloading fetches a
 *             fresh index.html with the current hashes.
 *   surface   the reload did not help, so the cause was never staleness. Fall through to
 *             the boundary, which explains it rather than showing a white page.
 *
 * The reload guard resets on every successful load. My first version set it once and
 * never cleared it, so a tab was rescued from the first deploy of its life and then left
 * to fail on every deploy after — the mechanism quietly stopped working exactly when it
 * had proved itself useful.
 */

const RELOAD_FLAG = 'app:chunk-reload';
const RETRIES = 2;
const RETRY_PAUSE_MS = 400;

/** Vite, webpack and Safari each word this differently; match the shapes, not one string. */
export const isChunkError = (error) => {
  const text = `${error?.name || ''} ${error?.message || ''}`;
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading (CSS )?chunk .* failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /error loading dynamically imported module/i.test(text)
  );
};

const flag = {
  get: () => { try { return sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { return true; } },
  set: () => { try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* private mode */ } },
  clear: () => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* private mode */ } },
};

/**
 * Cleared once anything has loaded successfully, which is the only honest signal that
 * the tab is current again. Exported so the boundary can call it after a good render
 * too — whichever happens first.
 */
export const markChunksHealthy = () => flag.clear();

const pause = (ms) => new Promise((r) => setTimeout(r, ms));

export const importWithRetry = async (factory) => {
  for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
    try {
      const mod = await factory();
      markChunksHealthy();
      return mod;
    } catch (error) {
      const last = attempt === RETRIES;
      if (!last && isChunkError(error)) { await pause(RETRY_PAUSE_MS * (attempt + 1)); continue; }
      if (!last) throw error; // not a chunk problem: no point retrying it

      /*
       * Out of retries. If this tab has not already tried reloading for this reason,
       * the chunk is genuinely missing and a reload will fetch the current index.html.
       * The promise is deliberately never resolved — the page is going away, and
       * resolving it would flash the error panel on the way out.
       */
      if (isChunkError(error) && !flag.get()) {
        flag.set();
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  }
  return undefined;
};

/** Drop-in for React.lazy. */
const lazyWithRetry = (factory) => React.lazy(() => importWithRetry(factory));

export default lazyWithRetry;
